import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { Modal } from "../../components/tailwind/modal/Modal";
import { Button } from "../../components/tailwind/button/Button";
import { FileDrop } from "../../components/FileDrop";
import { Text } from "../../components/Text";
import { RootState } from "../../utils/store";
import { showMessage } from "../../utils/partialUtils";
import { NotificationAppearance } from "../../api/djangoToastSlice";
import { reportBug, Reproducibility, Severity } from "../../api/reportBugApi";
import { validateFileExtension } from "../../utils/files";

type Props = {
  onClose: () => void;
};

const SEVERITY_OPTIONS: { value: Severity; label: string }[] = [
  { value: "annoying", label: "Annoying, but I am not blocked" },
  { value: "somewhat_blocked", label: "I am somewhat blocked in my work" },
  { value: "completely_blocked", label: "I am completely blocked in my work" },
];

const REPRODUCIBILITY_OPTIONS: { value: Reproducibility; label: string }[] = [
  { value: "always", label: "Always" },
  { value: "sometimes", label: "Only sometimes" },
];

// Keep in sync with SCREENSHOT_ALLOWED_CONTENT_TYPES in customer_report_bug.py
const BUG_REPORT_SCREENSHOT_EXTENSIONS = ["png", "jpg", "jpeg"];
const BUG_REPORT_SCREENSHOT_MIME_TYPES = ["image/png", "image/jpeg"];
const BUG_REPORT_SCREENSHOT_MAX_SIZE_BYTES = 5 * 1024 * 1024;

export const BugReportModal: React.FC<Props> = ({ onClose }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const token = useSelector((state: RootState) => state.auth.token);

  const [description, setDescription] = useState("");
  const [
    reproducibility,
    setReproducibility,
  ] = useState<Reproducibility | null>(null);
  const [stepsToReproduce, setStepsToReproduce] = useState("");
  const [documentExamples, setDocumentExamples] = useState("");
  const [severity, setSeverity] = useState<Severity | null>(null);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isScreenshotInvalid = useMemo((): boolean => {
    if (!screenshot) {
      return false;
    }
    return (
      !validateFileExtension(screenshot, BUG_REPORT_SCREENSHOT_EXTENSIONS) ||
      !BUG_REPORT_SCREENSHOT_MIME_TYPES.includes(screenshot.type) ||
      screenshot.size > BUG_REPORT_SCREENSHOT_MAX_SIZE_BYTES
    );
  }, [screenshot]);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  useEffect(() => {
    if (!screenshot || isScreenshotInvalid) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(screenshot);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshot, isScreenshotInvalid]);

  const handleClose = (): void => {
    dialogRef.current?.close();
    onClose();
  };

  const isValid =
    description.trim().length > 0 &&
    reproducibility !== null &&
    severity !== null &&
    !isScreenshotInvalid;

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    if (
      !isValid ||
      reproducibility === null ||
      severity === null ||
      !token ||
      isSubmitting
    ) {
      return;
    }
    setIsSubmitting(true);
    try {
      const reference = await reportBug(token, {
        description: description.trim(),
        reproducibility,
        steps_to_reproduce: stepsToReproduce.trim(),
        document_examples: documentExamples.trim(),
        severity,
        screenshot,
      });
      showMessage(
        NotificationAppearance.SUCCESS,
        `Thanks! Your bug report (${reference}) has been sent to our support team.`
      );
      handleClose();
    } catch {
      // The API error handler already surfaces a toast.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal dialogRef={dialogRef} className="tw-max-w-lg">
      <Modal.Header divide>
        <h5
          className="tw-text-lg tw-font-semibold"
          data-testid="bug-report-modal-header"
        >
          Report a bug
        </h5>
      </Modal.Header>
      <Modal.Body className="tw-py-6">
        <form
          id="bug-report-form"
          className="txu-form tw-flex tw-flex-col tw-gap-4"
          onSubmit={handleSubmit}
        >
          <div>
            <label className="tw-text-lg" htmlFor="bug-report-description">
              Describe the problem you are experiencing *
            </label>
            <textarea
              id="bug-report-description"
              data-testid="bug-report-description"
              value={description}
              onChange={(e): void => setDescription(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="tw-text-lg">
              Does this happen always or only sometimes? *
            </label>
            {REPRODUCIBILITY_OPTIONS.map((option) => (
              <div key={option.value}>
                <input
                  type="radio"
                  id={`bug-report-reproducibility-${option.value}`}
                  data-testid={`bug-report-reproducibility-${option.value}`}
                  name="bug-report-reproducibility"
                  checked={reproducibility === option.value}
                  onChange={(): void => setReproducibility(option.value)}
                />
                <label
                  className="tw-text-lg"
                  htmlFor={`bug-report-reproducibility-${option.value}`}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>

          <div>
            <label className="tw-text-lg" htmlFor="bug-report-steps">
              Steps to reproduce, describe how we can encounter the same issue *
            </label>
            <textarea
              id="bug-report-steps"
              data-testid="bug-report-steps"
              value={stepsToReproduce}
              onChange={(e): void => setStepsToReproduce(e.target.value)}
            />
          </div>

          <div>
            <label className="tw-text-lg">Screenshot (optional)</label>
            <FileDrop
              selectedFile={screenshot}
              isInvalid={isScreenshotInvalid}
              onChange={setScreenshot}
              contentUnselected={
                <Text>Accepted formats: PNG, JPG, WebP, GIF. Max 5 MB.</Text>
              }
              contentSelected={
                previewUrl && !isScreenshotInvalid ? (
                  <div className="tw-mt-2">
                    <img
                      src={previewUrl}
                      alt="Screenshot preview"
                      data-testid="bug-report-screenshot-preview"
                      className="tw-max-h-40 tw-max-w-full tw-object-contain tw-mx-auto tw-rounded tw-border tw-border-gray-200"
                    />
                  </div>
                ) : null
              }
            />
          </div>

          <div>
            <label className="tw-text-lg" htmlFor="bug-report-documents">
              If it is related to Documents, provide one or more links and SKU
            </label>
            <textarea
              id="bug-report-documents"
              data-testid="bug-report-documents"
              value={documentExamples}
              onChange={(e): void => setDocumentExamples(e.target.value)}
            />
          </div>

          <div>
            <label className="tw-text-lg">How severe is this? *</label>
            {SEVERITY_OPTIONS.map((option) => (
              <div key={option.value}>
                <input
                  type="radio"
                  id={`bug-report-severity-${option.value}`}
                  data-testid={`bug-report-severity-${option.value}`}
                  name="bug-report-severity"
                  checked={severity === option.value}
                  onChange={(): void => setSeverity(option.value)}
                />
                <label
                  className="tw-text-lg"
                  htmlFor={`bug-report-severity-${option.value}`}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </form>
      </Modal.Body>
      <Modal.Actions>
        <Button variant="secondary" onClick={handleClose} type="button">
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="bug-report-form"
          data-testid="bug-report-submit"
          loading={isSubmitting}
          disabled={!isValid}
        >
          Send bug report
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
